import { useMemo } from "react";

/**
 * BucketWearMap — cinematic top-down view of a digger bucket where each
 * Tooth / Lip Shroud / Wing Shroud position is drawn as a wear-coloured cell.
 *
 * Props:
 *   items     — array of {id, label, kind, status} where kind === "wear_pct"
 *   onSelect  — (item) => void — optional click handler on a cell
 *   compact   — bool, renders a smaller footprint (default false)
 */
export default function BucketWearMap({ items, onSelect, compact = false }) {
  const grouped = useMemo(() => {
    const g = { tooth: [], lip: [], wing: [] };
    (items || []).forEach((it) => {
      const L = String(it.label || "").toLowerCase();
      if (L.includes("wing")) g.wing.push(it);
      else if (L.includes("lip")) g.lip.push(it);
      else if (L.includes("tooth")) g.tooth.push(it);
    });
    const posNum = (s) => {
      const m = String(s || "").match(/(\d+)/);
      return m ? Number(m[1]) : 0;
    };
    Object.values(g).forEach((arr) => arr.sort((a, b) => posNum(a.label) - posNum(b.label)));
    return g;
  }, [items]);

  const colorFor = (status) => {
    const s = String(status ?? "").toLowerCase();
    if (s === "" || s === "new" || s === "0") return { fill: "#4ade80", label: "NEW", text: "#0a2a12" };
    if (s === "25") return { fill: "#4ade80", label: "25%", text: "#0a2a12" };
    if (s === "50") return { fill: "#facc15", label: "50%", text: "#3a2f0d" };
    if (s === "75") return { fill: "#ffb347", label: "75%", text: "#4a2a06" };
    if (s === "100") return { fill: "#ff9b9b", label: "C/O", text: "#5a0f0f" };
    return { fill: "#2a2a2a", label: "—", text: "#71717a" };
  };

  if (grouped.tooth.length === 0 && grouped.lip.length === 0 && grouped.wing.length === 0) {
    return null;
  }

  const size = compact ? 0.7 : 1;
  const CELL = 46 * size;
  const GAP = 6 * size;
  const teeth = grouped.tooth;
  const lips = grouped.lip;
  const wings = grouped.wing;

  // Board width driven by max positions across categories
  const maxCols = Math.max(6, teeth.length, lips.length + 2 /* wings hug the ends */);
  const width = maxCols * (CELL + GAP) + GAP + 16;
  const height = (compact ? 190 : 240) * 1;

  return (
    <div
      className="panel-flat p-4 md:p-5 card-spotlight"
      data-testid="bucket-wear-map"
      style={{ overflow: "visible" }}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="text-[10px] uppercase tracking-[0.3em] font-black text-muted">Bucket teeth · wear map</div>
        <div className="ml-auto flex flex-wrap gap-1.5">
          {[
            { l: "NEW", c: "#4ade80" },
            { l: "25%", c: "#4ade80" },
            { l: "50%", c: "#facc15" },
            { l: "75%", c: "#ffb347" },
            { l: "C/O", c: "#ff9b9b" },
          ].map((k) => (
            <span
              key={k.l}
              className="pill !text-[9px] !py-0.5 !px-1.5 tabular-nums"
              style={{
                background: `${k.c}22`,
                color: k.c,
                border: `1px solid ${k.c}55`,
              }}
            >
              {k.l}
            </span>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          className="block mx-auto"
          style={{ maxWidth: "100%", height: "auto" }}
        >
          {/* Bucket outline — a soft parallelogram evoking a top-down bucket view */}
          <defs>
            <linearGradient id="bucketBody" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#181818" />
              <stop offset="100%" stopColor="#0a0a0a" />
            </linearGradient>
            <filter id="wearGlow" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="2.4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <path
            d={`
              M 16 30
              L ${width - 16} 30
              L ${width - 32} ${height - 22}
              L 32 ${height - 22} Z
            `}
            fill="url(#bucketBody)"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="1"
          />
          {/* Lip line */}
          <line
            x1="26"
            x2={width - 26}
            y1={height - 60}
            y2={height - 60}
            stroke="rgba(245,130,32,0.35)"
            strokeWidth="1"
            strokeDasharray="4 6"
          />
          {/* Wing shrouds — flank the ends */}
          {wings.map((w, i) => {
            const isRight = i >= Math.floor(wings.length / 2);
            const idxInSide = isRight ? i - Math.floor(wings.length / 2) : i;
            const cx = isRight
              ? width - 30 - idxInSide * (CELL + GAP)
              : 30 + idxInSide * (CELL + GAP);
            const cy = 60;
            const c = colorFor(w.status);
            return (
              <g
                key={w.id}
                transform={`translate(${cx - CELL / 2}, ${cy - CELL / 2})`}
                style={{ cursor: onSelect ? "pointer" : "default" }}
                onClick={() => onSelect?.(w)}
              >
                <rect width={CELL} height={CELL} rx="8" fill={c.fill} opacity="0.9" filter="url(#wearGlow)" />
                <text x={CELL / 2} y={CELL / 2 + 2} textAnchor="middle" fontSize={9} fontWeight="900" fill={c.text}>
                  W{idxInSide + 1}
                </text>
                <text x={CELL / 2} y={CELL / 2 + 14} textAnchor="middle" fontSize={8} fontWeight="700" fill={c.text}>
                  {c.label}
                </text>
              </g>
            );
          })}
          {/* Lip shrouds — one row above lip line */}
          {lips.map((l, i) => {
            const total = lips.length;
            const startX = width / 2 - ((total - 1) * (CELL + GAP)) / 2;
            const cx = startX + i * (CELL + GAP);
            const cy = height - 90;
            const c = colorFor(l.status);
            return (
              <g
                key={l.id}
                transform={`translate(${cx - CELL / 2}, ${cy - CELL / 2})`}
                style={{ cursor: onSelect ? "pointer" : "default" }}
                onClick={() => onSelect?.(l)}
              >
                <rect width={CELL} height={CELL} rx="8" fill={c.fill} opacity="0.85" filter="url(#wearGlow)" />
                <text x={CELL / 2} y={CELL / 2 + 2} textAnchor="middle" fontSize={9} fontWeight="900" fill={c.text}>
                  L{i + 1}
                </text>
                <text x={CELL / 2} y={CELL / 2 + 14} textAnchor="middle" fontSize={8} fontWeight="700" fill={c.text}>
                  {c.label}
                </text>
              </g>
            );
          })}
          {/* Teeth — the front cutting edge */}
          {teeth.map((t, i) => {
            const total = teeth.length;
            const startX = width / 2 - ((total - 1) * (CELL + GAP)) / 2;
            const cx = startX + i * (CELL + GAP);
            const cy = height - 30;
            const c = colorFor(t.status);
            return (
              <g
                key={t.id}
                transform={`translate(${cx - CELL / 2}, ${cy - CELL / 2})`}
                style={{ cursor: onSelect ? "pointer" : "default" }}
                onClick={() => onSelect?.(t)}
              >
                {/* Trapezoid shape — evokes an actual tooth */}
                <polygon
                  points={`${CELL * 0.15},0 ${CELL * 0.85},0 ${CELL},${CELL} 0,${CELL}`}
                  fill={c.fill}
                  filter="url(#wearGlow)"
                />
                <text x={CELL / 2} y={CELL * 0.45} textAnchor="middle" fontSize={10} fontWeight="900" fill={c.text}>
                  T{i + 1}
                </text>
                <text x={CELL / 2} y={CELL * 0.72} textAnchor="middle" fontSize={9} fontWeight="800" fill={c.text}>
                  {c.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
